import { useState } from "react";
import { api } from "../../lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../ui/dialog";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import ItemCard from "./ItemCard";
import { toast } from "sonner";
import { PackageOpen, Coins, Puzzle } from "lucide-react";
import { playSfxByTag } from "../../lib/soundBus";

const DENOMS = ["pp", "gp", "sp", "cp"];

export default function ChestLootModal({ open, onOpenChange, gameId, chestToken, character, onCharacterUpdate }) {
  const [riddleGuess, setRiddleGuess] = useState("");
  const [solving, setSolving] = useState(false);
  const [puzzleFailed, setPuzzleFailed] = useState(false);
  if (!chestToken) return null;
  const loot = chestToken.loot || [];
  const coins = chestToken.chest_currency || {};
  const hasCoins = DENOMS.some((d) => coins[d]);
  const puzzleLock = chestToken.puzzle_lock;
  const puzzleUnsolved = puzzleLock && !puzzleLock.solved;

  const attemptPuzzle = async () => {
    setSolving(true);
    try {
      const { data } = await api.post(`/games/${gameId}/tokens/${chestToken.id}/attempt-puzzle`, { riddle_guess: riddleGuess });
      setPuzzleFailed(!data.solved);
      if (data.solved) toast.success("Puzzle solved — the chest opens!");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not attempt the puzzle");
    } finally {
      setSolving(false);
    }
  };


  const take = async (idx) => {
    if (!character) return toast.error("You need a character to loot items");
    try {
      const item = loot[idx];
      const { data } = await api.post(`/characters/${character.id}/loot-from-chest`, {
        game_id: gameId, token_id: chestToken.id, item_index: idx,
      });
      onCharacterUpdate?.(data.character);
      if (item?.rarity === "legendary") {
        playSfxByTag(gameId, "legendary_loot");
        toast.success(`A legendary find — ${item.name}!`, { duration: 4000 });
      } else {
        toast.success("Added to your inventory");
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not loot item");
    }
  };

  const takeCoins = async () => {
    if (!character) return toast.error("You need a character to loot coins");
    try {
      const { data } = await api.post(`/characters/${character.id}/loot-currency-from-chest`, {
        game_id: gameId, token_id: chestToken.id,
      });
      onCharacterUpdate?.(data.character);
      toast.success("Coins added to your purse");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not loot coins");
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#322216] border-white/10 text-[#f1e0c5] max-w-md" data-testid="chest-loot-modal">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl flex items-center gap-2">
            <PackageOpen className="w-5 h-5 text-[#D4AF37]" /> {chestToken.name}
          </DialogTitle>
          <DialogDescription>You open the chest.</DialogDescription>
        </DialogHeader>
        {puzzleUnsolved ? (
          <div className="space-y-2 bg-black/20 border border-[#D4AF37]/30 rounded-lg p-3" data-testid="chest-puzzle-section">
            <p className="text-xs text-[#D4AF37] flex items-center gap-1.5"><Puzzle className="w-3.5 h-3.5" /> This chest has a puzzle lock.</p>
            {puzzleLock.type === "riddle" ? (
              <>
                <p className="text-sm italic">{puzzleLock.riddle_question}</p>
                <Input data-testid="chest-puzzle-riddle-input" value={riddleGuess} onChange={(e) => setRiddleGuess(e.target.value)} placeholder="Your answer…" />
              </>
            ) : (
              <p className="text-xs text-[#f1e0c5]/50">Requires: {(puzzleLock.required_item_names || []).join(", ")} in your inventory.</p>
            )}
            {puzzleFailed && <p className="text-xs text-[#D90429]" data-testid="chest-puzzle-failed">Not quite — try again.</p>}
            <Button data-testid="chest-puzzle-attempt-button" onClick={attemptPuzzle} disabled={solving} className="w-full rounded-full bg-[#D4AF37] text-black hover:bg-[#c19d2e]">Attempt Puzzle</Button>
          </div>
        ) : (
          <>
            {hasCoins && (
              <div className="flex items-center justify-between bg-black/20 border border-[#D4AF37]/30 rounded-lg px-3 py-2" data-testid="chest-loot-coins">
                <span className="text-sm text-[#D4AF37] flex items-center gap-1.5">
                  <Coins className="w-4 h-4" /> {DENOMS.filter((d) => coins[d]).map((d) => `${coins[d]} ${d}`).join(", ")}
                </span>
                <Button data-testid="chest-take-coins-button" onClick={takeCoins} size="sm" className="rounded-full bg-[#D90429] hover:bg-[#b8031f] text-xs">Take</Button>
              </div>
            )}
            <div className="space-y-2 max-h-96 overflow-y-auto scrollbar-thin">
              {loot.length === 0 && !hasCoins && <p className="text-xs text-[#f1e0c5]/40 italic">The chest is empty.</p>}
              {loot.map((item, idx) => (
                <ItemCard
                  key={idx}
                  testId={`chest-loot-item-${idx}`}
                  card={item}
                  actions={<Button data-testid={`chest-take-button-${idx}`} onClick={() => take(idx)} size="sm" className="rounded-full bg-[#D90429] hover:bg-[#b8031f] text-xs">Take</Button>}
                />
              ))}
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
